import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../components/AppIcon';
import AlertDialog from '../../../../components/ui/AlertDialog';
import { appDate } from '../../../../utils/appDateFormat';
import { DateField } from '../../../../components/dateFields';
import {
  getGoalMilestones,
  addGoalMilestone,
  updateGoalMilestone,
  deleteGoalMilestone,
  toggleGoalMilestone,
  getGoalOutcomeMetrics,
  GoalMilestoneView,
  GoalMilestoneInput,
  GoalOutcomeMetricsView,
} from '../../../../api/PreformanceManagement/Goals';

interface MilestonePanelProps {
  goalId: string;
  goalStartDate: string;
  goalDueDate: string;
  isOpen: boolean;
  canEdit: boolean;
}

// Store translation keys; call t(label) at render time
const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: string; labelKey: string }> = {
  Pending: { color: 'text-muted-foreground', bg: 'bg-muted', icon: 'Clock', labelKey: 'Pending' },
  InProgress: { color: 'text-primary', bg: 'bg-primary/10', icon: 'Play', labelKey: 'In Progress' },
  Completed: { color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle', labelKey: 'Completed' },
  Overdue: { color: 'text-error', bg: 'bg-error/10', icon: 'AlertTriangle', labelKey: 'Overdue' },
};

const MilestonePanel: React.FC<MilestonePanelProps> = ({
  goalId,
  goalStartDate,
  goalDueDate,
  isOpen,
  canEdit,
}) => {
  const { t } = useTranslation();
  const [milestones, setMilestones] = useState<GoalMilestoneView[]>([]);
  const [metrics, setMetrics] = useState<GoalOutcomeMetricsView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<GoalMilestoneInput>({
    title: '',
    description: '',
    isCompleted: false,
    status: 1,
    dueDate: '',
    order: 0,
  });

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [milestonesData, metricsData] = await Promise.allSettled([
        getGoalMilestones(goalId),
        getGoalOutcomeMetrics(goalId),
      ]);
      if (milestonesData.status === 'fulfilled') setMilestones(milestonesData.value);
      if (metricsData.status === 'fulfilled') setMetrics(metricsData.value);
      // allSettled swallows rejections, so a refused read has to be surfaced explicitly.
      const rejected = [milestonesData, metricsData].filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );
      if (rejected.length) {
        console.error('Failed to load milestone data:', rejected[0].reason);
        setError(rejected[0].reason instanceof Error ? rejected[0].reason.message : t('Something went wrong'));
      }
    } catch (err) {
      console.error('Failed to load milestone data:', err);
      setError(err instanceof Error ? err.message : t('Something went wrong'));
    } finally {
      setLoading(false);
    }
  }, [goalId, t]);

  useEffect(() => {
    if (isOpen && goalId) {
      loadData();
    }
  }, [isOpen, goalId, loadData]);

  const resetForm = () => {
    setFormData({ title: '', description: '', isCompleted: false, status: 1, dueDate: '', order: 0 });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleAdd = async () => {
    if (!formData.title.trim()) return;
    setError(null);
    try {
      await addGoalMilestone(goalId, { ...formData, order: milestones.length });
      resetForm();
      await loadData();
    } catch (err) {
      console.error('Failed to add milestone:', err);
      setError(err instanceof Error ? err.message : t('Failed to add milestone'));
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !formData.title.trim()) return;
    setError(null);
    try {
      await updateGoalMilestone(editingId, formData);
      resetForm();
      await loadData();
    } catch (err) {
      console.error('Failed to update milestone:', err);
      setError(err instanceof Error ? err.message : t('Failed to update milestone'));
    }
  };

  // Rethrows so the confirmation dialog stays open and reports the failure itself.
  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await deleteGoalMilestone(id);
      await loadData();
    } catch (err) {
      console.error('Failed to delete milestone:', err);
      throw err;
    }
  };

  const handleToggle = async (id: string) => {
    setError(null);
    try {
      await toggleGoalMilestone(id);
      await loadData();
    } catch (err) {
      console.error('Failed to toggle milestone:', err);
      setError(err instanceof Error ? err.message : t('Something went wrong'));
    }
  };

  const startEdit = (m: GoalMilestoneView) => {
    setEditingId(m.id);
    setShowAddForm(false);
    const statusNum = m.status === 'Pending' ? 1 : m.status === 'InProgress' ? 2 : m.status === 'Completed' ? 3 : 4;
    setFormData({
      title: m.title,
      description: m.description || '',
      isCompleted: m.isCompleted,
      status: statusNum,
      dueDate: m.dueDate ? m.dueDate.split('T')[0] : '',
      order: m.order,
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return appDate(dateString);
  };

  const getTimelinePosition = (date?: string) => {
    if (!date || !goalStartDate || !goalDueDate) return 0;
    const start = new Date(goalStartDate).getTime();
    const end = new Date(goalDueDate).getTime();
    const point = new Date(date).getTime();
    const range = end - start;
    if (range <= 0) return 0;
    return Math.max(0, Math.min(100, ((point - start) / range) * 100));
  };

  const getProjectionStatusConfig = (status: string) => {
    switch (status) {
      case 'OnTrack': return { color: 'text-success', bg: 'bg-success/10', label: t('On Track') };
      case 'AtRisk': return { color: 'text-warning', bg: 'bg-warning/10', label: t('At Risk') };
      case 'Behind': return { color: 'text-error', bg: 'bg-error/10', label: t('Behind') };
      case 'Completed': return { color: 'text-success', bg: 'bg-success/10', label: t('Completed') };
      default: return { color: 'text-muted-foreground', bg: 'bg-muted', label: status };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12" data-testid="milestone-panel-loading">
        <AppIcon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  const renderForm = (isEdit: boolean) => (
    <div className="p-4 bg-muted/30 rounded-lg border border-border space-y-3" data-testid={isEdit ? 'milestone-edit-form' : 'milestone-add-form'}>
      <input
        type="text"
        value={formData.title}
        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
        placeholder={t('Milestone title')}
        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid="milestone-title-input"
      />
      <textarea
        value={formData.description || ''}
        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
        placeholder={t('Description (optional)')}
        rows={2}
        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
        data-testid="milestone-description-input"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Due Date')}</label>
          <DateField
            value={formData.dueDate || ''}
            min={goalStartDate?.split('T')[0]}
            max={goalDueDate?.split('T')[0]}
            onChange={(v) => setFormData(prev => ({ ...prev, dueDate: v }))}
            aria-label={t('Due Date')}
            data-testid="milestone-duedate-input"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Status')}</label>
          <select
            value={formData.status || 1}
            onChange={(e) => setFormData(prev => ({ ...prev, status: parseInt(e.target.value) }))}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            data-testid="milestone-status-select"
          >
            <option value={1}>{t('Pending')}</option>
            <option value={2}>{t('In Progress')}</option>
            <option value={3}>{t('Completed')}</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end space-x-2 rtl:space-x-reverse">
        <button
          onClick={resetForm}
          className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors"
          data-testid="milestone-cancel-btn"
        >
          {t('Cancel')}
        </button>
        <button
          onClick={isEdit ? handleUpdate : handleAdd}
          disabled={!formData.title.trim()}
          className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          data-testid="milestone-save-btn"
        >
          {isEdit ? t('Update') : t('Add')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="milestone-panel">
      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-2 rounded mb-3" data-testid="milestone-error">{error}</div>
      )}

      {/* Outcome Metrics */}
      {metrics && (
        <div className="space-y-4" data-testid="outcome-metrics">
          <h3 className="font-medium text-foreground">{t('Outcome Metrics')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 bg-muted/30 rounded-lg border border-border" data-testid="metric-milestone-progress">
              <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                <AppIcon name="Flag" size={14} className="text-primary" />
                <span className="text-xs font-medium text-muted-foreground">{t('Milestone Progress')}</span>
              </div>
              <p className="text-lg font-semibold text-foreground">{metrics.milestoneProgressPercent}%</p>
              <p className="text-xs text-muted-foreground">
                {metrics.completedMilestones}/{metrics.totalMilestones} {t('completed')}
              </p>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg border border-border" data-testid="metric-cadence">
              <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                <AppIcon name="RefreshCw" size={14} className="text-primary" />
                <span className="text-xs font-medium text-muted-foreground">{t('Review Cadence')}</span>
              </div>
              <p className="text-lg font-semibold text-foreground">{metrics.cadenceAdherencePercent}%</p>
              <p className="text-xs text-muted-foreground">
                {metrics.actualReviews}/{metrics.expectedReviews} {t('reviews')}
              </p>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg border border-border" data-testid="metric-velocity">
              <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                <AppIcon name="TrendingUp" size={14} className="text-warning" />
                <span className="text-xs font-medium text-muted-foreground">{t('Completion Projection')}</span>
              </div>
              {(() => {
                const ps = getProjectionStatusConfig(metrics.projectionStatus);
                return (
                  <>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ps.bg} ${ps.color}`}>
                      {ps.label}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {metrics.daysRemaining > 0 ? `${metrics.daysRemaining} ${t('days remaining')}` : t('Past due')}
                    </p>
                    {metrics.projectedCompletionDate && (
                      <p className="text-xs text-muted-foreground">
                        {t('Est.')}: {formatDate(metrics.projectedCompletionDate)}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Timeline Visualization */}
      {milestones.length > 0 && (
        <div className="space-y-2" data-testid="milestone-timeline">
          <h3 className="font-medium text-foreground">{t('Timeline')}</h3>
          <div className="relative py-4 px-2">
            <div className="relative h-2 bg-muted rounded-full">
              {(() => {
                const todayPos = getTimelinePosition(new Date().toISOString());
                return todayPos > 0 && todayPos < 100 ? (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-primary z-10"
                    style={{ left: `${todayPos}%` }}
                    title={t('Today')}
                  />
                ) : null;
              })()}
              {milestones.map((m) => {
                const pos = getTimelinePosition(m.dueDate);
                return (
                  <div
                    key={m.id}
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-white shadow"
                    style={{ left: `${pos}%`, backgroundColor: m.status === 'Completed' ? 'var(--color-success)' : m.status === 'Overdue' ? 'var(--color-error)' : m.status === 'InProgress' ? 'var(--color-primary)' : 'var(--color-muted-foreground)' }}
                    title={`${m.title}: ${formatDate(m.dueDate)}`}
                    data-testid={`timeline-dot-${m.id}`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-muted-foreground">{formatDate(goalStartDate)}</span>
              <span className="text-xs text-muted-foreground">{formatDate(goalDueDate)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Milestones List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-foreground">
            {t('Milestones')} {milestones.length > 0 && <span className="text-muted-foreground font-normal">({milestones.length})</span>}
          </h3>
          {canEdit && !showAddForm && !editingId && (
            <button
              onClick={() => { setShowAddForm(true); setEditingId(null); resetForm(); setShowAddForm(true); }}
              className="flex items-center space-x-1 rtl:space-x-reverse text-sm text-primary hover:text-primary/80 transition-colors"
              data-testid="add-milestone-btn"
            >
              <AppIcon name="Plus" size={14} />
              <span>{t('Add Milestone')}</span>
            </button>
          )}
        </div>

        {showAddForm && renderForm(false)}

        {milestones.length === 0 && !showAddForm ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="milestone-empty">
            <AppIcon name="Flag" size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">{t('No milestones yet')}</p>
            {canEdit && (
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-2 text-sm text-primary hover:text-primary/80"
              >
                {t('Add your first milestone')}
              </button>
            )}
          </div>
        ) : (
          milestones.map((m) => {
            const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.Pending;

            if (editingId === m.id) {
              return <div key={m.id}>{renderForm(true)}</div>;
            }

            return (
              <div
                key={m.id}
                className="flex items-start space-x-3 rtl:space-x-reverse p-3 bg-muted/30 rounded-lg border border-border group hover:border-primary/20 transition-colors"
                data-testid={`milestone-item-${m.id}`}
              >
                {canEdit ? (
                  <button
                    onClick={() => handleToggle(m.id)}
                    className="mt-0.5 flex-shrink-0"
                    data-testid={`milestone-toggle-${m.id}`}
                  >
                    <AppIcon
                      name={m.isCompleted ? 'CheckCircle' : 'Circle'}
                      size={18}
                      className={m.isCompleted ? 'text-success' : 'text-muted-foreground hover:text-primary'}
                    />
                  </button>
                ) : (
                  <AppIcon
                    name={m.isCompleted ? 'CheckCircle' : 'Circle'}
                    size={18}
                    className={m.isCompleted ? 'text-success' : 'text-muted-foreground'}
                  />
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <p className={`text-sm font-medium ${m.isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {m.title}
                    </p>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                      {t(cfg.labelKey)}
                    </span>
                  </div>
                  {m.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>
                  )}
                  <div className="flex items-center space-x-3 rtl:space-x-reverse mt-1">
                    {m.dueDate && (
                      <span className="text-xs text-muted-foreground flex items-center space-x-1 rtl:space-x-reverse">
                        <AppIcon name="Calendar" size={10} />
                        <span>{t('Due')}: {formatDate(m.dueDate)}</span>
                      </span>
                    )}
                    {m.completedDate && (
                      <span className="text-xs text-success flex items-center space-x-1 rtl:space-x-reverse">
                        <AppIcon name="Check" size={10} />
                        <span>{t('Completed')}: {formatDate(m.completedDate)}</span>
                      </span>
                    )}
                  </div>
                </div>

                {canEdit && (
                  <div className="flex items-center space-x-1 rtl:space-x-reverse opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => startEdit(m)}
                      className="p-1 hover:bg-muted rounded"
                      data-testid={`milestone-edit-${m.id}`}
                    >
                      <AppIcon name="Edit" size={14} className="text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => setPendingDeleteId(m.id)}
                      className="p-1 hover:bg-destructive/10 rounded"
                      data-testid={`milestone-delete-${m.id}`}
                    >
                      <AppIcon name="Trash2" size={14} className="text-destructive" />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => { if (!open) setPendingDeleteId(null); }}
        title={t('goals.milestone.delete.title')}
        description={t('goals.milestone.delete.description')}
        confirmLabel={t('goals.milestone.delete.confirm')}
        cancelLabel={t('Cancel')}
        variant="destructive"
        onConfirm={async () => {
          if (pendingDeleteId) await handleDelete(pendingDeleteId);
        }}
      />
    </div>
  );
};

export default MilestonePanel;
